import { useEffect } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { ShieldCheck, Sprout, UtensilsCrossed, MapPin, ArrowRight } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import MarqueeStrip from "./MarqueeStrip";
import { useWaitlist } from "../context/WaitlistContext";

const FOREST = "#163A2A";
const INK = "#16362A";
const LEAF = "#2F6B3F";
const MINT = "#8FC69B";
const CREAM = "#FAF7F2";
const BODY = "#4A6054";
const SAGE = "#E9F0E7";

const INGREDIENTS = [
  {
    id: "sprouted-moong",
    name: "Sprouted Moong",
    line: "Slow-sprouted for easy-to-digest plant protein.",
    powers: "Chillas",
    img: "https://static.prod-images.emergentagent.com/jobs/002aba5c-2c34-4dde-9d34-f67edaa196a8/images/dc3456534c2829bc15df5e4a1c7408b598f98e4251440559972a0f87ae79666c.jpeg",
  },
  {
    id: "native-millets",
    name: "Native Millets",
    line: "Ragi, jowar & bajra — ancient grains, real fiber.",
    powers: "Noodles · Pancakes",
    img: "https://static.prod-images.emergentagent.com/jobs/002aba5c-2c34-4dde-9d34-f67edaa196a8/images/af013d85c2f07510bdfbff29955d31acd692d43c257ebcd13e302cb570493f10.jpeg",
  },
  {
    id: "steel-cut-oats",
    name: "Steel-Cut Oats",
    line: "Wholegrain, never refined. Clean, steady energy.",
    powers: "Pancakes · Chillas",
    img: "https://static.prod-images.emergentagent.com/jobs/002aba5c-2c34-4dde-9d34-f67edaa196a8/images/ca1b1bbab788efced62cb3eebc95155732cc4135ea025d7caaedf9d4b8bcd75b.jpeg",
  },
  {
    id: "chickpea-besan",
    name: "Chickpea & Besan",
    line: "Protein-dense and naturally gluten-free.",
    powers: "Chillas · Pastas",
    img: "https://static.prod-images.emergentagent.com/jobs/002aba5c-2c34-4dde-9d34-f67edaa196a8/images/97c358bbd06dc0b3e310179ad864dda916cf992d999110d020051ef9070af98e.jpeg",
  },
  {
    id: "golden-flaxseed",
    name: "Golden Flaxseed",
    line: "Omega-3s and gut-loving fiber in every spoon.",
    powers: "Across the range",
    img: "https://static.prod-images.emergentagent.com/jobs/002aba5c-2c34-4dde-9d34-f67edaa196a8/images/a1efe4afa212e7fe72a2705aad41fb43edd10b61abf27513cbd122ece73ea689.jpeg",
  },
  {
    id: "buckwheat-kuttu",
    name: "Buckwheat (Kuttu)",
    line: "A hearty pseudo-grain that carries pasta & noodles.",
    powers: "Pastas · Noodles",
    img: "https://static.prod-images.emergentagent.com/jobs/002aba5c-2c34-4dde-9d34-f67edaa196a8/images/ccf2b15c7741b45ac582275351c9dbdc1e4f765b4695d88e5a741a68016b04c8.jpeg",
  },
];

const PILLARS = [
  {
    id: "clean-label",
    icon: ShieldCheck,
    title: "Clean Label, Always",
    desc: "No refined flour, no palm oil, no preservatives. If you can't pronounce it, it's not in here.",
  },
  {
    id: "protein-fiber",
    icon: Sprout,
    title: "Protein + Fiber First",
    desc: "Every pack is built for real nutrition — up to 20g protein and 8g fiber, never empty calories.",
  },
  {
    id: "everyday-food",
    icon: UtensilsCrossed,
    title: "Everyday, Not “Health Food”",
    desc: "Chillas, pancakes, pasta, noodles — the foods you already love, made clean.",
  },
  {
    id: "made-in-india",
    icon: MapPin,
    title: "Made Fresh in India",
    desc: "Small batches and local grains, with full control from sourcing right to the pack.",
  },
];

export default function OurStory() {
  const { openModal } = useWaitlist();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.location.hash === "#nosmooth") return;
    const lenis = new Lenis({
      duration: 1.3,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App" data-testid="our-story-page">
      <Header />
      <main>
        {/* Intro */}
        <section
          data-testid="story-intro"
          style={{ backgroundColor: CREAM }}
          className="px-6 sm:px-10 lg:px-16 pt-20 sm:pt-28 pb-16 sm:pb-20"
        >
          <div className="max-w-4xl mx-auto">
            <p
              className="story-rise text-[11px] font-bold uppercase tracking-[0.28em] mb-6"
              style={{ color: LEAF, animationDelay: "0.05s" }}
            >
              Why We Started
            </p>
            <h1
              data-testid="story-main-headline"
              className="story-rise text-[2.6rem] sm:text-6xl lg:text-[4.4rem] tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: INK, animationDelay: "0.18s" }}
            >
              We believe everyday foods <span style={{ color: "#306B3F" }}>deserve better.</span>
            </h1>
            <div
              data-testid="story-intro-subtitle"
              className="story-rise mt-[3.9rem] max-w-xl"
              style={{ animationDelay: "0.42s" }}
            >
              <p className="text-lg sm:text-xl leading-relaxed" style={{ color: "#306B3F" }}>
                We believe every plate should be filled with clean ingredients, rich in protein &amp; fiber, and{" "}
                <em style={{ color: "#306B3F" }}><strong>freshly FLAVRD.</strong></em>
              </p>
              <button
                data-testid="story-intro-shop-btn"
                onClick={openModal}
                className="story-rise mt-9 group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 active:scale-95"
                style={{ backgroundColor: "#163A2A", color: "#FAF7F2", animationDelay: "0.52s" }}
              >
                Shop your Protein
                <ArrowRight size={15} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </section>

        <MarqueeStrip />

        {/* Our Vision — forest block */}
        <section
          data-testid="story-vision"
          style={{ backgroundColor: FOREST }}
          className="px-6 sm:px-10 lg:px-16 py-24 sm:py-32"
        >
          <div className="max-w-3xl mx-auto text-center">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-[11px] font-bold uppercase tracking-[0.28em] mb-7"
              style={{ color: MINT }}
            >
              Our Vision
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.06 }}
              className="text-2xl sm:text-[2.4rem] lg:text-[2.72rem] leading-[1.14] text-left"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: CREAM }}
            >
              Everyday foods with clean ingredients,{" "}
              <span style={{ color: "#FFFFFF" }}>and</span>{" "}
              <span style={{ color: MINT }}>zero compromise</span>{" "}
              <span style={{ color: "#FFFFFF" }}>on taste.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.18 }}
              className="text-base sm:text-lg leading-relaxed mt-8 max-w-xl text-left"
              style={{ color: "rgba(250,247,242,0.82)" }}
            >
              All our products are high in plant protein and dietary fiber.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.28 }}
              className="mt-10 text-left"
            >
              <button
                data-testid="story-vision-waitlist-btn"
                onClick={openModal}
                className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 active:scale-95"
                style={{ backgroundColor: MINT, color: FOREST }}
              >
                Join the Waitlist
                <ArrowRight size={15} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          </div>
        </section>

        {/* Hero Ingredients */}
        <section
          id="ingredients"
          data-testid="story-ingredients"
          style={{ backgroundColor: CREAM }}
          className="px-6 sm:px-10 lg:px-16 py-20 sm:py-28"
        >
          <div className="max-w-6xl mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-[11px] font-bold uppercase tracking-[0.26em] mb-4"
              style={{ color: LEAF }}
            >
              What Goes In
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.06 }}
              className="text-4xl sm:text-5xl lg:text-[3.2rem] leading-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: INK }}
            >
              Our hero ingredients.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="text-base sm:text-lg mb-14 max-w-xl"
              style={{ color: "#386546" }}
            >
              We only use better-for-you ingredients that are clean, healthy and high in protein &amp; fiber.
            </motion.p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
              {INGREDIENTS.map((ing, i) => (
                <IngredientCard key={ing.id} ing={ing} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section
          id="why-us"
          data-testid="story-why-us"
          style={{ backgroundColor: SAGE }}
          className="px-6 sm:px-10 lg:px-16 py-20 sm:py-28"
        >
          <div className="max-w-6xl mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-[11px] font-bold uppercase tracking-[0.26em] mb-4"
              style={{ color: LEAF }}
            >
              Why Flavrd
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.06 }}
              className="text-4xl sm:text-5xl lg:text-[3.2rem] leading-tight mb-14 max-w-2xl"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: INK }}
            >
              A better way to make everyday food.
            </motion.h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              {PILLARS.map((p, i) => (
                <PillarCard key={p.id} pillar={p} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          data-testid="story-cta"
          style={{ backgroundColor: FOREST }}
          className="px-6 sm:px-10 lg:px-16 py-24 sm:py-32"
        >
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.1] mb-7"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: CREAM }}
            >
              Be part of the first batch.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-base sm:text-lg leading-relaxed mb-10 max-w-lg mx-auto"
              style={{ color: "rgba(250,247,242,0.82)" }}
            >
              We're launching our first clean-label range soon. Join the waitlist and taste the
              difference before anyone else.
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.18 }}
              onClick={openModal}
              data-testid="story-cta-button"
              className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 active:scale-95"
              style={{ backgroundColor: MINT, color: FOREST }}
            >
              Shop your Protein
              <ArrowRight size={18} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function IngredientCard({ ing, index }) {
  return (
    <motion.div
      data-testid={`ingredient-card-${ing.id}`}
      initial={{ opacity: 0, y: 34 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: (index % 3) * 0.09 }}
      whileHover={{ y: -6 }}
      className="range-card rounded-2xl overflow-hidden flex flex-col"
      style={{ backgroundColor: "#FFFFFF", border: "1px solid rgba(47,107,63,0.12)" }}
    >
      <div className="relative" style={{ aspectRatio: "1 / 1", overflow: "hidden" }}>
        <img src={ing.img} alt={ing.name} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="px-5 pt-5 pb-6 flex flex-col flex-1">
        <h3 className="text-xl leading-snug mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: INK }}>
          {ing.name}
        </h3>
        <p className="text-sm leading-relaxed mb-5 flex-1" style={{ color: BODY }}>
          {ing.line}
        </p>
        <span
          className="self-start text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full"
          style={{ backgroundColor: SAGE, color: LEAF }}
        >
          {ing.powers}
        </span>
      </div>
    </motion.div>
  );
}

function PillarCard({ pillar, index }) {
  const Icon = pillar.icon;
  return (
    <motion.div
      data-testid={`pillar-card-${pillar.id}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: (index % 2) * 0.1 }}
      className="rounded-2xl p-7 sm:p-8 flex flex-col"
      style={{ backgroundColor: CREAM, border: "1px solid rgba(47,107,63,0.12)" }}
    >
      <div
        className="grid place-items-center rounded-full mb-5"
        style={{ width: "3rem", height: "3rem", backgroundColor: FOREST }}
      >
        <Icon size={22} strokeWidth={2} color={MINT} />
      </div>
      <h3 className="text-xl sm:text-2xl leading-snug mb-2.5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: INK }}>
        {pillar.title}
      </h3>
      <p className="text-sm sm:text-base leading-relaxed" style={{ color: BODY }}>
        {pillar.desc}
      </p>
    </motion.div>
  );
}
